import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import createUpdateInspectorAccount from 'src/api/createUpdateInspectorAccount';
import AccountLoader from 'src/pages/authentication/components/inspectorAccount/AccountLoader';
import iconStyles from 'src/components/css/iconStyle';
import LogoDT from 'src/icons/LogoDT';
import { UserInspectorResource, InspectorStatus } from 'src/types/userAccount';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Link as RouterLink } from 'react-router-dom';
import { formatLocalDate } from 'src/utils/datetimeHelper';
import ErrorMessage from 'src/pages/authentication/components/ErrorMessage';
import { keyframes } from '@mui/material';

const shakeAnimation = keyframes({
  '0%': { transform: 'scale(1)' },
  '50%': { transform: 'scale(1.2)' },
  '100%': { transform: 'scale(1)' },
});

const emailValidation = /^[a-z\d]+(\.[a-z\d]+|_[a-z\d]+)*$/i;
const specialCharacters = /[^a-zA-Z\d\s/._]/i;
const incorrectStartOrEnd = /^[^a-z\d]|[^a-z\d]$/i;
const whiteSpaces = /\s+/;

const inspectorFormSchema = z.object({
  inspectorEmail: z
    .string()
    .min(1, 'Debe ingresar su usuario')
    .refine((val) => !whiteSpaces.test(val), {
      message: 'No ingrese espacios en blanco',
    })
    .refine((val) => !specialCharacters.test(val), {
      message: 'No ingrese caracteres especiales',
    })
    .refine((val) => !incorrectStartOrEnd.test(val), {
      message: 'No debe empezar o terminar con caracteres especiales',
    })
    .refine((val) => emailValidation.test(val), {
      message: 'El usuario no es válido',
    }),
});

type InspectorFormValues = z.infer<typeof inspectorFormSchema>;

interface InspectorFormProps {
  resetPassword: (email: string, expirationDate: string) => React.ReactNode;
}

const InspectorForm = ({ resetPassword }: InspectorFormProps) => {
  const [inspectorState, setInspectorState] =
    useState<UserInspectorResource | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<InspectorFormValues>({
    resolver: zodResolver(inspectorFormSchema),
    defaultValues: {
      inspectorEmail: '',
    },
  });

  const onSubmit = async (data: InspectorFormValues) => {
    try {
      const res = await createUpdateInspectorAccount(
        `${data.inspectorEmail.toLowerCase()}@dt.gob.cl`,
        false
      );
      if (res.status === 200 || res.status === 201) {
        setInspectorState(res.data);
      } else {
        setInspectorState({
          email: data.inspectorEmail.toLowerCase(),
          status: InspectorStatus.ERROR,
          expirationDate: null,
        });
      }
    } catch (err) {
      setInspectorState({
        email: data.inspectorEmail.toLowerCase(),
        status: InspectorStatus.ERROR,
        expirationDate: null,
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  if (isSubmitting) {
    return <AccountLoader isInspector />;
  }

  if (inspectorState?.status === InspectorStatus.ERROR) {
    return (
      <ErrorMessage errorMessage="Ha ocurrido un error inesperado. Por favor intente nuevamente.">
        <Button
          id="retry-button"
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          onClick={() => setInspectorState(null)}
          sx={{ marginTop: '16px' }}
        >
          Reintentar
        </Button>
      </ErrorMessage>
    );
  }

  if (
    inspectorState?.status === InspectorStatus.SUCCESSFULLY_ACTIVATED &&
    inspectorState.email !== undefined
  ) {
    return (
      <>
        <Typography
          color="textPrimary"
          variant="h4"
          marginBottom={3}
        >
          ¡Cuenta Activada!
        </Typography>
        <CheckCircleRoundedIcon
          color="primary"
          style={iconStyles}
        />
        <Typography>
          <Typography
            color="textPrimary"
            variant="body1"
            marginTop={3}
            marginX="auto"
          >
            {`Su contraseña ha sido enviada a ${inspectorState.email.toLowerCase()}`}
          </Typography>
          <Typography
            color="textPrimary"
            variant="body1"
            marginTop={2}
            marginX="auto"
            sx={{ width: '100%' }}
          >
            Esta expirará automáticamente el día
            <span
              style={{
                fontWeight: 500,
                display: 'inline-block',
              }}
            >
              {inspectorState.expirationDate !== null
                ? formatLocalDate(inspectorState.expirationDate)
                : ''}
            </span>
          </Typography>
        </Typography>
        <Button
          id="login-button"
          component={RouterLink}
          to="/loginMailInspector"
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          sx={{ marginTop: 2 }}
        >
          Iniciar Sesión
        </Button>
      </>
    );
  }

  if (
    inspectorState?.status === InspectorStatus.ALREADY_ACTIVATED &&
    resetPassword
  ) {
    return resetPassword(
      inspectorState.email ?? '',
      inspectorState.expirationDate === null
        ? ''
        : formatLocalDate(inspectorState.expirationDate)
    );
  }

  return (
    <form
      id="inspector-form"
      onSubmit={handleSubmit(onSubmit)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
      }}
      noValidate
      autoComplete="off"
    >
      <Typography
        color="textPrimary"
        variant="h4"
        marginBottom={3}
      >
        Acceso Inspector
      </Typography>
      <LogoDT style={iconStyles} />
      <TextField
        id="inspector-input"
        {...register('inspectorEmail')}
        autoFocus
        fullWidth
        label="Usuario"
        margin="normal"
        type="text"
        variant="outlined"
        autoComplete="off"
        error={Boolean(touchedFields.inspectorEmail && errors.inspectorEmail)}
        InputProps={{
          endAdornment: <Typography>@dt.gob.cl</Typography>,
        }}
      />
      {touchedFields.inspectorEmail && errors.inspectorEmail && (
        <Typography
          color="error"
          id="inspector-input-error"
          variant="caption"
          marginBottom={3}
          sx={{
            position: 'absolute',
            left: '5px',
            top: '75px',
            animation: `${shakeAnimation} 0.3s ease-in-out`,
          }}
        >
          {errors.inspectorEmail.message}
        </Typography>
      )}
      <Button
        id="submit-button"
        color="primary"
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        disabled={isSubmitting}
        sx={{
          marginTop: '18px',
        }}
      >
        Enviar Contraseña
      </Button>
    </form>
  );
};

export default InspectorForm;
